<template>
  <BApp>
    <div
      id="app"
      class="app"
    >
      <!-- Modal for displaying generic messages to the user -->
      <section name="topSection">
        <GenericInfoModal
          v-if="showGenericInfoModal"
          :header="genericInfoModal.header"
          :message="genericInfoModal.message"
        />

        <TopBar id="topBar" />
      </section>

      <section id="content-section">
        <router-view
          id="main-router-view"
          :key="$route.path"
        />
      </section>
      <section id="bottom-section">
        <div
          v-if="!userHasMadeTrackingConsentDecision"
          id="cookie-consent-banner-div"
        >
          <cookie-banner-vue
            @tracking-accepted="handleConsent"
            @tracking-declined="handleNoConsent"
          />
          <!-- <button @click="handleConsent()">Accept Tracking</button> -->
        </div>
        <TrialsFooter
          class="main-footer"
          @manage-tracking-consent-clicked="revokeConsentDecision"
        />
      </section></div
  ></BApp>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { useStorage } from '@vueuse/core';
import TopBar from '@/components/Navigation/TopBar.vue';

const { locale, availableLocales } = useI18n({ useScope: 'global' });

const setI18nLocale = () => {
  const defaultLocale = 'de';
  //Reactively access localStorage property and set default ("de") if it isn't present.
  const storedLocale = useStorage('locale', defaultLocale);
  if (availableLocales.includes(storedLocale.value)) {
    //Makes sure the localStorage property is one of the allowed locales
    //Set value from local storage to i18n locale
    locale.value = storedLocale.value;
  } else {
    //Stored value isn't valid. Set localStorage property and i18n locale to default locale.
    storedLocale.value = defaultLocale;
    locale.value = defaultLocale;
  }
};

//Load stored language setting or set default
setI18nLocale();
</script>

<script>
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';
import { BApp } from 'bootstrap-vue-next';
import { mapState } from 'pinia';
import { useGeneralFlagStore } from './stores/generalFlagStore';
import { useGeneralStore } from './stores/generalStore';
import GenericInfoModal from './components/Modals/GenericInfoModal.vue';
import TrialsFooter from '@/components/General/Footer.vue';
import mainFhirResourceSetter from '@/helperScripts/fhirHelpers/initialization/mainFhirResourceSetter';
import CookieBannerVue from '@/components/General/CookieBanner.vue';
import greetingPrinter from './helperScripts/generalUtils/greetingPrinter';
import {
  removeKeycloakStateQueryFromGivenUrl,
  routeContainsKeycloakParameters,
} from '@/router';
import { usefilterProfileStore } from './stores/filterProfileStore';

export default {
  name: 'App',

  components: { TrialsFooter, CookieBannerVue, GenericInfoModal },

  data() {
    return {
      userHasMadeTrackingConsentDecision: false,
    };
  },

  computed: {
    ...mapState(useGeneralStore, ['genericInfoModal']),
    ...mapState(useGeneralFlagStore, ['showGenericInfoModal']),
  },

  async mounted() {
    document.title = 'CATS - MOLIT Institute';

    if (routeContainsKeycloakParameters(this.$route)) {
      this.removeKeycloakParamsFromUrl();
    }
  },

  created() {
    //Print greeting to console
    greetingPrinter.printGreeting();
    //Load large and/or often-used resources from FHIR server
    mainFhirResourceSetter.initFhirResourcesInStore();
    //Load user search profile
    usefilterProfileStore().initialize();
    //Read from localStorage if user has made a decision for tracking consent.
    this.userHasMadeTrackingConsentDecision =
      this.getConsentDecisionMadeFromLocalStorage() === 'true';
  },

  methods: {
    /**
     * Removes keycloak parameters from url.
     * This has been made necessary because keycloak-js appends session info to the url when first entering the page.
     * Together with the filter param mechanism (params are passed via url), this results in two problems:
     * 1) Making the url longer, thereby running into the url size limit faster
     * 2) Leading to a page reload when entering the first filter parameter, because the route path changes, which has been used as a key in a router view in the past. This is a valid mechanism, as described here: https://router.vuejs.org/guide/essentials/dynamic-matching.html#reacting-to-params-changes
     */
    removeKeycloakParamsFromUrl() {
      //TODO In future versions: Check if this mechanism is still necessary or if keycloak or router updates have fixed the problem.
      //Remove keycloak parameters from url
      const cleanedUrl = removeKeycloakStateQueryFromGivenUrl(this.$route.path);
      //Re-append search parameters to url if necessary
      const queryParams = this.$route.query?.params;
      //Update url without triggering reload
      if (queryParams && queryParams !== '') {
        const cleanedUrlWithParamsArgument = `${cleanedUrl}?params=${queryParams}`;
        history.pushState({}, '', cleanedUrlWithParamsArgument);
      } else {
        history.pushState({}, '', cleanedUrl);
      }
    },

    handleConsent() {
      if (this.$matomo) {
        //Provided by matomo
        this.$matomo.rememberConsentGiven();

        this.setConsentDecisionMadeInLocalStorage();
        this.updateLocalConsentMadeVariableFromLocalStorage(); //Update local variable to reflect change in localStorage
      }
    },

    handleNoConsent() {
      this.setConsentDecisionMadeInLocalStorage(); //Remember that the decision has been made
      this.updateLocalConsentMadeVariableFromLocalStorage(); //Update local variable to reflect change in localStorage
    },

    setConsentDecisionMadeInLocalStorage() {
      localStorage.setItem('consentDecisionMade', 'true');
    },

    getConsentDecisionMadeFromLocalStorage() {
      return localStorage.getItem('consentDecisionMade');
    },

    clearConsentDecisionMadeInLocalStorage() {
      localStorage.setItem('consentDecisionMade', 'false');
    },

    revokeConsentDecision() {
      if (this.$matomo) {
        //Provided by matomo
        this.$matomo.forgetConsentGiven();

        this.clearConsentDecisionMadeInLocalStorage();
        this.updateLocalConsentMadeVariableFromLocalStorage(); //Update local variable to reflect change in localStorage
      }
    },

    updateLocalConsentMadeVariableFromLocalStorage() {
      //Allows quasi reactive behaviour
      this.userHasMadeTrackingConsentDecision =
        this.getConsentDecisionMadeFromLocalStorage() === 'true'; //Update local variable to reflect change in localStorage
    },
  },
};
</script>

<style lang="scss">
@import '@/scss/__variables.scss';
@import '@/scss/baseStyle.scss';
#app {
  height: 100vh;
  min-height: 0;
  margin: 0;
  padding: 0;
  box-sizing: border-box;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
}

#topBar {
  border-bottom: solid 2px $lighter-grey;
  height: $top-bar-height;
  padding-top: 0;
  margin-top: 0;
  z-index: 2;
  opacity: 1;
}

#content-section {
  min-height: 0;
  min-width: 0;
  overflow: auto;
}
#main-router-view {
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: auto;
}

// The footer element grows with its content (see Footer.vue); the
// background is painted by Footer.vue on the <footer> element itself.
// No extra bottom padding here: it used to leave an empty strip below
// the footer band at the bottom of the page.
.main-footer {
  height: $footer-height;
  margin: 0;
}

#bottom-section {
  width: 100%;
  z-index: 9;
}

@media screen and (max-width: 1280px) {
  #app {
    font-size: small; //Reduce font size for smaller resolutions in order to fit more content
  }
}
</style>
